import { createElement, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import Button from 'components/field/Button';

import useAccessControl from 'hooks/useAccessControl';
import useDebounce from 'hooks/useDebounce';
import useUpdateEffect from 'hooks/useUpdateEffect';
import useWindowSize from 'hooks/useWindowSize';

import * as S from './styled/Tabs.styled';

/**
 * @author Burak Ak & Burak Unsal
 * @description In addition, the component specified in the object returns to you in the same order.
 */
const Tabs = ({ data: dataProp, index, setIndex, checkURL = false }) => {
	const { pathname } = useLocation();
	const navigate = useNavigate();
	const isAuthorized = useAccessControl();
	const data = dataProp.filter((item) => isAuthorized(item.path));
	const [tabIndex, setTabIndex] = useState(index ?? 0);
	const [sliderDimensions, setSliderDimensions] = useState({ width: 0, left: 0 });

	const dataIndex = data.findIndex((item) => item.path === pathname);
	const urlMode = dataIndex !== -1;

	useEffect(() => {
		if (checkURL && !urlMode) {
			const path = data[tabIndex]?.path;
			if (path) navigate(path);
		}
	}, [checkURL, pathname]);

	const selectedTabIndex = urlMode ? dataIndex : tabIndex;

	useUpdateEffect(() => {
		setIndex && setIndex(selectedTabIndex);
	}, [selectedTabIndex]);

	const MemoizedComponent = useMemo(
		() => (data[selectedTabIndex]?.component ? createElement(data[selectedTabIndex]?.component) : undefined),
		[selectedTabIndex]
	);

	return (
		<>
			<S.Container className="tabs">
				<S.ItemContainer>
					{data?.map((tab, index) => (
						<TabItem
							key={index}
							index={index}
							selected={selectedTabIndex === index}
							setSliderDimensions={setSliderDimensions}
							urlMode={urlMode}
							setTabIndex={setTabIndex}
							error={tab.error}
							{...tab}
						/>
					))}
				</S.ItemContainer>
				<S.Slider width={sliderDimensions.width} sliderLeft={sliderDimensions.left} />
			</S.Container>
			{MemoizedComponent}
		</>
	);
};

const TabItem = (props) => {
	const { label, path, selected, index, setSliderDimensions, urlMode, setTabIndex, error } = props;

	const navigate = useNavigate();
	const buttonRef = useRef();
	const { windowWidth } = useWindowSize();
	const debouncedWindowWidth = useDebounce(windowWidth, 500);
	const localization = useSelector((state) => state.layout.localization);

	const calculateSliderDimensions = () => {
		const link = buttonRef.current;
		const sliderPosition =
			document.body.dir === 'rtl'
				? link?.offsetParent?.offsetWidth - link?.offsetLeft - link?.offsetWidth
				: link?.offsetLeft;
		setSliderDimensions({
			width: link?.offsetWidth,
			left: sliderPosition
		});
	};

	useEffect(() => {
		if (selected) {
			calculateSliderDimensions();
		}
	}, [selected, debouncedWindowWidth]);

	useUpdateEffect(() => {
		if (selected) {
			setTimeout(() => {
				calculateSliderDimensions();
			}, 200); // delay for loading new page with selected language
		}
	}, [localization]);

	const handleClick = () => {
		if (urlMode) {
			navigate(path);
		} else {
			setTabIndex(index);
		}
	};

	return (
		<Button
			ref={buttonRef}
			variant="borderless"
			css={S.ButtonCss({
				selected,
				error
			})}
			label={label}
			onClick={handleClick}
		/>
	);
};

export default Tabs;
